import type { WidgetOptions } from './types';

type HeightOption = WidgetOptions['minHeight'];
type HeightMap = Exclude<HeightOption, string | undefined>;

interface ViewportInfo {
  isMobile: boolean;
  isTablet: boolean;
  isZoomed: boolean;
  viewportHeight: number;
  isSmallViewport: boolean;
}

function detectViewport(): ViewportInfo {
  const devicePixelRatio = window.devicePixelRatio || 1;
  const zoomLevel = Math.round((devicePixelRatio / 1) * 100);
  const viewportHeight = window.innerHeight;
  const width = window.innerWidth;

  return {
    isMobile: width < 640,
    isTablet: width >= 640 && width < 1024,
    isZoomed: zoomLevel > 100 || devicePixelRatio > 1.1,
    viewportHeight,
    isSmallViewport: viewportHeight < 700,
  };
}

function shouldUseCompactMode(options: WidgetOptions, viewport: ViewportInfo): boolean {
  return Boolean(
    options.compactMode ||
      (options.adaptiveHeight !== false && (viewport.isZoomed || viewport.isSmallViewport)),
  );
}

function applyDefaultHeights(wrapper: HTMLElement, compact: boolean, viewportHeight: number): void {
  if (compact) {
    // Compact mode: use the available height minus a small margin
    wrapper.style.minHeight = 'auto';
    wrapper.style.height = 'auto';
    wrapper.style.maxHeight = `${viewportHeight - 20}px`;
    wrapper.style.overflowY = 'auto';
  } else {
    // Normal mode: dvh instead of vh for better support
    wrapper.style.minHeight = 'clamp(500px, 80dvh, 900px)';
    wrapper.style.height = 'auto';
    wrapper.style.maxHeight = 'clamp(600px, 90dvh, 100dvh)';
  }
  wrapper.style.transition = 'height 0.3s ease-out, min-height 0.3s ease-out';
}

function pickByBreakpoint(map: HeightMap, viewport: ViewportInfo): string | undefined {
  if (viewport.isMobile && map.mobile) return map.mobile;
  if (viewport.isTablet && map.tablet) return map.tablet;
  return map.desktop || undefined;
}

function applyMinHeight(wrapper: HTMLElement, value: HeightOption, viewport: ViewportInfo): void {
  if (!value) return;
  if (typeof value !== 'string') {
    const picked = pickByBreakpoint(value, viewport);
    if (picked) wrapper.style.minHeight = picked;
    return;
  }
  wrapper.style.minHeight = value;
}

function applyMaxHeight(wrapper: HTMLElement, value: HeightOption, viewport: ViewportInfo): void {
  if (!value) return;
  const picked = typeof value === 'string' ? value : pickByBreakpoint(value, viewport);
  if (!picked) return;
  wrapper.style.maxHeight = picked;
  wrapper.style.height = picked;
  wrapper.style.overflowY = 'auto';
}

export function applyResponsiveStyles(container: HTMLElement, options: WidgetOptions): void {
  const viewport = detectViewport();
  const wrapper = container.querySelector('.widget-wrapper') as HTMLElement;
  if (!wrapper) return;

  const compact = shouldUseCompactMode(options, viewport);
  wrapper.classList.toggle('widget-compact-mode', compact);

  const hasHeightOptions = Boolean(options.minHeight || options.maxHeight);
  if (options.fullHeight !== true && !hasHeightOptions) {
    applyDefaultHeights(wrapper, compact, viewport.viewportHeight);
  }

  applyMinHeight(wrapper, options.minHeight, viewport);
  // A string minHeight without maxHeight keeps a 2/3 viewport height (dvh)
  if (typeof options.minHeight === 'string' && options.minHeight && !options.maxHeight) {
    wrapper.style.height = '66.67dvh';
    wrapper.style.maxHeight = '66.67dvh';
  }
  applyMaxHeight(wrapper, options.maxHeight, viewport);

  if (options.scrollBehavior) {
    wrapper.style.scrollBehavior = options.scrollBehavior;
  }
}
